/* eslint-disable no-nested-ternary */
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import BillingPaymentOptions, {
  BILLING_OPTIONS,
} from '../../../../../commonComponents/shop/BillingPaymentOptions';
import LoggedUserContext from '../../../../../contextProviders/LoggedUserContext';
import { handleBillingAddressInOrder } from '../../../../../services/utilityFunctions';
import { addressReducer } from '../../../../../commonComponents/modal/AcademyPaymentModal';
import ShopPriceEstimation from '../../../../shop/shopItems/ShopPriceEstimation';
import OrderWizard from '../../../../../commonComponents/modal/components/OrderWizard';
import PlanProp, {
  STEP_PLAN_CHECKOUT,
} from '../../../../plan/propTypes/planProp';
import Notifications from '../../../../../services/notifications';

const initialStateAddress = {
  billingAddress: {},
  showAddressForm: false,
};

const BillingStep = ({
  t,
  tenant,
  countries,
  setBillingAddress,
  step,
  selectedPlanOrderDetails,
  _csrf,
}) => {
  const { loggedUser } = useContext(LoggedUserContext);

  useEffect(() => {
    const address = handleBillingAddressInOrder(
      loggedUser,
      loggedUser?.address?.address_1 ? loggedUser.address : {},
    );
    setBillingAddress(address);
  }, [loggedUser]);

  const personalInfo = loggedUser?.personalInfo;

  const isIndividual =
    personalInfo?.businessType === 'individual' || !personalInfo?.businessType;

  const companyName = personalInfo?.companyName || null;

  const initialAddress = handleBillingAddressInOrder(
    loggedUser,
    loggedUser?.address?.address_1 ? loggedUser?.address : {},
  );

  const [checkoutAddress, dispatchAddressSection] = useReducer(
    addressReducer,
    initialStateAddress,
    () => ({
      ...initialStateAddress,
      billingAddress: {
        ...initialAddress,
        companyName: !isIndividual ? companyName : null,
      },
      countryCode: initialAddress.countryCode,
    }),
  );

  const { billingAddress, showAddressForm } = checkoutAddress;

  const handleAddressForm = useCallback(show => {
    dispatchAddressSection({ type: 'showAddressForm', payload: show });
  }, []);

  const handleAddressChange = useCallback(
    fieldParam => value => {
      dispatchAddressSection({
        type: 'updateAddress',
        payload: {
          [fieldParam]: value,
        },
      });
    },

    [checkoutAddress],
  );

  const saveAddress = useCallback(() => {
    const data = { _csrf, address: checkoutAddress.billingAddress };
    if (checkoutAddress?.billingAddress?.phone) {
      data.phone = checkoutAddress?.billingAddress?.phone;
    }
    axios({
      url: '/api/user/me',
      method: 'PUT',
      data,
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Organization',
      },
    })
      .then(({ data: response }) => {
        setBillingAddress(response?.data?.address);
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchProductsList');
        console.log(err);
      });
    dispatchAddressSection({ type: 'showAddressForm', payload: false });
  }, [checkoutAddress]);

  return (
    <>
      <div className="details">
        <div className="order-wizard-container">
          <OrderWizard
            activeStep={
              step === STEP_PLAN_CHECKOUT.ORDER
                ? 1
                : step === STEP_PLAN_CHECKOUT.BILLING
                ? 2
                : 3
            }
          />
          <div className="billing-container">
            <BillingPaymentOptions
              t={t}
              tenant={tenant}
              countries={countries}
              saveAddress={saveAddress}
              orderAlreadyCreated={false}
              showPaymentByCompany={false}
              billingAddress={billingAddress}
              isPaidByCompanyAvailable={false}
              showAddressForm={showAddressForm}
              handleAddressForm={handleAddressForm}
              handleAddressChange={handleAddressChange}
              billingActive={BILLING_OPTIONS.YOUR_BILLING}
            />
          </div>
        </div>
        <div className="sideBar-wrapper">
          <div className="subscription-plan-modal-popup-price-info">
            <ShopPriceEstimation
              frequency={t(
                `chartPortlet.${selectedPlanOrderDetails?.interval}`,
              )}
              orderDetails={{
                pricesWithoutVAT:
                  selectedPlanOrderDetails?.userPrice.priceWithoutVAT,
                total: selectedPlanOrderDetails?.userPrice?.total ?? 0,
                subtotal: selectedPlanOrderDetails?.userPrice?.price ?? 0,
                symbol: selectedPlanOrderDetails?.userPrice.symbol,
                tax: selectedPlanOrderDetails?.userPrice?.vat || null,
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

BillingStep.propTypes = {
  t: PropTypes.func.isRequired,
  tenant: PropTypes.shape({}).isRequired,
  countries: PropTypes.shape([]).isRequired,
  setBillingAddress: PropTypes.func.isRequired,
  selectedPlanOrderDetails: PropTypes.shape(PlanProp.propType).isRequired,
  step: PropTypes.string.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default BillingStep;
